import { useEffect, useState, type FormEvent } from 'react'
import { observer } from 'mobx-react-lite'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { TELEGRAM_BOT_ID, readTelegramResult, telegramAuthUrl } from '@/shared/lib/telegram'
import { Button } from '@/shared/ui/Button'
import { Field } from '@/shared/ui/Field'
import { Strip } from '@/shared/ui/Strip'
import { KeyIcon, LockIcon, MailIcon, TelegramIcon } from '@/shared/ui/icons'
import { useAuth } from '@/stores/RootStore'
import { AuthCard, AuthLayout, authStyles as styles } from './AuthLayout'
import { useMeta } from '@/shared/lib/hooks/useMeta'

/**
 * Вход. Два шага в одном экране: пароль, затем второй фактор,
 * если он включён.
 *
 * Ветвление решает бекенд, а не мы: на `/auth/login` он отвечает либо
 * токеном, либо требованием второго фактора. Гадать на фронте, нужен ли
 * второй шаг, значит однажды пустить без него.
 */
export const LoginPage = observer(function LoginPage() {
  useMeta('Вход')
  const auth = useAuth()
  const location = useLocation()

  // Куда человек шёл до того, как его отправили сюда. Без этого вход
  // всегда высаживает на обзор: открыл ссылку на конкретный вывод,
  // ввёл пароль — и ищи этот вывод заново среди тысячи строк.
  // Только внутренние пути: `from` приходит из истории браузера,
  // а туда можно положить и «https://чужой-сайт».
  const back = typeof location.state === 'object' && location.state !== null
    ? (location.state as { from?: unknown }).from
    : undefined
  // Без `from` админ высаживается на СВОЙ обзор (/app/admin), а не на
  // кабинет мерчанта: у admin он тоже есть (админ — такой же аккаунт
  // с флагом), но в нём нули, и после входа это читается как «пропали
  // комиссии», хотя они на соседней странице.
  const home = auth.isAdmin ? '/app/admin' : '/app'
  const target = typeof back === 'string' && back.startsWith('/') && !back.startsWith('//')
    ? back
    : home

  if (auth.authenticated) return <Navigate to={target} replace />

  return (
    <AuthLayout>
      {auth.step.kind === 'two-factor' ? <TwoFactorForm /> : <PasswordForm />}
    </AuthLayout>
  )
})

const PasswordForm = observer(function PasswordForm() {
  const auth = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void auth.login(email.trim(), password)
  }

  // Telegram возвращает браузер сюда с подписанными данными в хеше URL.
  // Читаем один раз при загрузке и сразу стираем хеш: он содержит
  // подпись входа, и ей незачем оставаться в истории браузера и в
  // адресной строке, пока человек уже в кабинете.
  useEffect(() => {
    const user = readTelegramResult(globalThis.location.hash)
    if (!user) return
    globalThis.history.replaceState(null, '', globalThis.location.pathname)
    void auth.loginTelegram(user)
  }, [auth])

  const telegramEnabled = TELEGRAM_BOT_ID !== ''
  const startTelegram = () => {
    globalThis.location.assign(telegramAuthUrl(`${globalThis.location.origin}/login`))
  }

  return (
    <AuthCard title="Вход в аккаунт" sub="Введите данные для входа">
      {auth.expired ? (
        <Strip tone="warn">Сессия истекла</Strip>
      ) : null}
      {auth.error ? <Strip tone="bad">{auth.error}</Strip> : null}

      <form className={styles.form} onSubmit={submit}>
        {/* Две группы с разными зазорами — как в макете: поля идут
            плотнее (16), между блоком полей и блоком действий — 24. */}
        <div className={styles.inputs}>
        <Field
          type="email"
          placeholder="Почта"
          icon={<MailIcon />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
        />
        <Field
          type="password"
          placeholder="Пароль"
          icon={<LockIcon />}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />

        <Link className={styles.forgot} to="/reset">Забыли пароль?</Link>
        </div>

        <div className={styles.actions}>
          {/*
            Кнопка НЕ гасится на пустой форме: в макете она нарисована
            активной, а поля помечены required — браузер сам покажет,
            какое из них пустое, и укажет на него курсором. Мёртвая
            кнопка молчит и не объясняет, чего от человека хотят.
            От двойной отправки защищает busy.
          */}
          <Button type="submit" block busy={auth.busy}>Войти</Button>

          <span className={styles.or}>или</span>

          {/* Кнопка из макета. Работает, когда задан VITE_TELEGRAM_BOT_ID:
              редирект на oauth.telegram.org и обратно с подписанными
              данными. Без id бота кнопка остаётся на месте, но неактивна:
              убирать её из макета нельзя, а показывать рабочей — тоже. */}
          <button type="button" className={styles.tg}
                  disabled={!telegramEnabled || auth.busy}
                  onClick={startTelegram}
                  title={telegramEnabled ? undefined : 'Вход через Telegram не подключён'}>
            <span className={styles.tgLabel}>Войти через Telegram</span>
            <TelegramIcon size={24} />
          </button>
        </div>
      </form>
    </AuthCard>
  )
})

const TwoFactorForm = observer(function TwoFactorForm() {
  const auth = useAuth()
  const [code, setCode] = useState('')

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void auth.submitTwoFactor(code)
  }

  return (
    <AuthCard
      title="Подтверждение"
      sub="Введите код из приложения двухфакторной аутентификации"
    >
      {auth.error ? <Strip tone="bad">{auth.error}</Strip> : null}

      <form className={styles.form} onSubmit={submit}>
        <Field
          label="Код подтверждения"
          placeholder="000000"
          icon={<KeyIcon />}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoComplete="one-time-code"
          inputMode="text"
          numeric
          autoFocus
          required
        />
        <Button type="submit" block busy={auth.busy} disabled={code.trim().length < 6}>
          Подтвердить
        </Button>
        <Button type="button" variant="secondary" block onClick={() => auth.backToPassword()}>
          Отмена
        </Button>
      </form>
    </AuthCard>
  )
})
